import React from 'react';
import { Button, Input } from 'semantic-ui-react';
import { errorInput } from '../../../components/helpers';
import {
  isCaptchaRequired,
  useRecaptchaToken,
  RECAPTCHA_SECURITY_ERROR,
} from '../../../components/helpers/recaptchaUtils';
import useRememberCredentials from '../../../components/helpers/useRememberCredentials';
import RememberCredentialsCheckbox from '../../../components/helpers/RememberCredentialsCheckbox';
import styled from '@emotion/styled';
import Swal from 'sweetalert2';
import guestService from 'src/services/api/guest';

const AcademyLoginContainer = styled.div`
  min-height: calc(100vh - 100px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0 calc(50% - 192px);
  margin: 0 auto;
`;

const LoginContainer = styled.div`
  background: white;
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
`;

const LoginTitle = styled.h1`
  text-align: center;
  color: #333;
  margin-bottom: 10px;
  font-size: 28px;
  span {
    font-size: 42px;
    vertical-align: sub;
  }
`;

const LoginSubtitle = styled.h2`
  text-align: center;
  color: #666;
  margin-bottom: 30px;
  font-size: 18px;
  font-weight: normal;
`;

const LoginForm = styled.form`
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const StyledInput = styled(Input)`
  width: 100% !important;
  margin: 10px 0 !important;

  &.error {
    border-color: #dc3545 !important;
  }
`;

const StyledButton = styled(Button)`
  width: 100% !important;
  margin: 10px 0 !important;
`;

const ErrorHeading = styled.h4`
  color: red;
`;

const AcademyLogin = (props) => {
  const [inputs, setInputs] = React.useState({
    identification: '',
  });
  const [errorInputs, setErrorInputs] = React.useState({});
  const getCaptchaToken = useRecaptchaToken('academy_login');
  const getGuestCreateCaptchaToken = useRecaptchaToken('guest_create');
  const { rememberCredentials, setRememberCredentials, persistCredentials } =
    useRememberCredentials({ setInputs });

  const { getStudentProgress, setLoading, loading, createAccount } = props;

  const verifyForm = () => {
    let mandatoryInputs = ['identification'];

    if (createAccount) {
      mandatoryInputs = [...mandatoryInputs, 'name', 'lastName', 'email', 'phone'];
    }

    let errorInputsCheck = {};

    mandatoryInputs.forEach((input) => {
      errorInputsCheck = errorInput({
        errorInputs: errorInputsCheck,
        input: { name: input, value: inputs[input] },
      });
    });

    if (inputs['identification'] && inputs['identification'].length <= 5) {
      errorInputsCheck = errorInput({
        errorInputs: errorInputsCheck,
        input: {
          name: 'identification',
          value: inputs['identification'].length >= 5,
        },
      });
    }

    if (createAccount && inputs['phone'] && inputs['phone'].length <= 10) {
      errorInputsCheck = errorInput({
        errorInputs: errorInputsCheck,
        input: {
          name: 'phone',
          value: inputs['phone'].length >= 10,
        },
      });
    }

    setErrorInputs(errorInputsCheck);
    return Object.values(errorInputsCheck).includes(true);
  };

  const resolveCaptcha = async (getToken = getCaptchaToken) => {
    const captchaRequired = isCaptchaRequired();
    const captcha = await getToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return null;
    }
    return { captcha, captchaRequired };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (verifyForm()) return;

    persistCredentials({
      identification: inputs.identification,
    });

    setLoading(true);
    const result = await resolveCaptcha();
    if (!result) {
      setLoading(false);
      return;
    }

    getStudentProgress({
      identification: inputs.identification,
      ...(result.captchaRequired ? { captcha: result.captcha } : {}),
    });
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (verifyForm()) return;
    setLoading(true);

    persistCredentials({
      identification: inputs.identification,
      phone: inputs.phone,
    });

    const createResult = await resolveCaptcha(getGuestCreateCaptchaToken);
    if (!createResult) {
      setLoading(false);
      return;
    }

    const { name, lastName, phone, identification, email } = inputs;
    guestService
      .createGuest({
        name,
        lastName,
        phone,
        identification,
        email,
        ...(createResult.captchaRequired ? { captcha: createResult.captcha } : {}),
      })
      .then(async (response) => {
        if (response.data.success) {
          const loginResult = await resolveCaptcha();
          if (!loginResult) {
            setLoading(false);
            return;
          }
          getStudentProgress({
            identification,
            ...(loginResult.captchaRequired ? { captcha: loginResult.captcha } : {}),
          });
          return;
        }

        Swal.fire({
          icon: 'warning',
          title: 'No se pudo crear la cuenta',
          text: response.data?.message || 'Verifica los datos e intenta de nuevo.',
        });
        setLoading(false);
      })
      .catch((error) => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Error al crear la cuenta. Intenta de nuevo.',
        });
        setLoading(false);
      });
  };

  const handleInputs = (event) => {
    const { name, value } = event.target;
    setInputs({ ...inputs, [name]: value });
    setErrorInputs((prev) => ({ ...prev, [name]: false }));
  };

  return (
    <AcademyLoginContainer>
      <LoginContainer>
        <LoginTitle>
          Crecer<span>+</span>
        </LoginTitle>
        <LoginSubtitle>
          {createAccount ? 'Para continuar debes crear una cuenta' : 'Iniciar Sesión'}
        </LoginSubtitle>

        <LoginForm onSubmit={createAccount ? handleCreateAccount : handleLogin}>
          {Object.values(errorInputs || {}).includes(true) && (
            <ErrorHeading>Por favor verifica los campos en rojo.</ErrorHeading>
          )}
          {createAccount && (
            <>
              <FormGroup>
                <StyledInput
                  onChange={handleInputs}
                  name="name"
                  value={inputs.name}
                  type="text"
                  placeholder="Nombre"
                  id="name"
                  className={errorInputs['name'] ? 'error' : ''}
                />
              </FormGroup>
              <FormGroup>
                <StyledInput
                  onChange={handleInputs}
                  name="lastName"
                  value={inputs.lastName}
                  type="text"
                  placeholder="Apellido"
                  id="lastName"
                  className={errorInputs['lastName'] ? 'error' : ''}
                />
              </FormGroup>
              <FormGroup>
                <StyledInput
                  onChange={handleInputs}
                  name="email"
                  value={inputs.email}
                  type="email"
                  placeholder="Email"
                  id="email"
                  className={errorInputs['email'] ? 'error' : ''}
                />
              </FormGroup>
              <FormGroup>
                <StyledInput
                  placeholder="Celular"
                  id="phone"
                  name="phone"
                  type="number"
                  className={errorInputs['phone'] ? 'error' : ''}
                  value={inputs.phone}
                  onChange={handleInputs}
                />
              </FormGroup>
            </>
          )}
          <FormGroup>
            <StyledInput
              onChange={handleInputs}
              name="identification"
              value={inputs.identification}
              type="number"
              placeholder="Identificación"
              id="identification"
              className={errorInputs['identification'] ? 'error' : ''}
            />
          </FormGroup>
          <RememberCredentialsCheckbox
            checked={rememberCredentials}
            onChange={setRememberCredentials}
          />

          <FormGroup>
            <StyledButton primary disabled={loading} type="submit">
              {loading ? 'Cargando...' : createAccount ? 'Crear Cuenta' : 'Iniciar Sesión'}
            </StyledButton>
          </FormGroup>
        </LoginForm>
      </LoginContainer>
    </AcademyLoginContainer>
  );
};

export default AcademyLogin;
